import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../../components/AppShell";
import { Card, CardBody } from "../../components/ui/Card";
import { api, ApiException } from "../../lib/api";
import { cn } from "../../lib/cn";
import { MembersPanel } from "./Members";
import { ProvidersPanel } from "./Providers";
import { LovedOnePanel } from "./LovedOne";
import { RequestsPanel } from "./Requests";

interface MeResponse {
  user: { id: string; is_platform_admin?: boolean } | null;
  circles: Array<{ id: string; name: string; role: "admin" | "member"; loved_one_name: string | null }>;
}

type Tab = "members" | "providers" | "loved_one" | "requests";

export function Settings() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const tab = (params.get("tab") as Tab) || "members";

  useEffect(() => {
    api
      .get<MeResponse>("/api/me")
      .then((data) => {
        if (!data.user) return navigate("/signin", { replace: true });
        const adminCircle = data.circles.find((c) => c.role === "admin");
        if (!adminCircle) return navigate("/app", { replace: true });
        setMe(data);
      })
      .catch((err) => {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading || !me) {
    return (
      <AppShell activeTab="settings">
        <div className="p-5 text-text-muted">Loading…</div>
      </AppShell>
    );
  }

  const circle = me.circles.find((c) => c.role === "admin")!;

  function setTab(next: Tab) {
    setParams({ tab: next });
  }

  return (
    <AppShell activeTab="settings">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:py-8">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-text-muted">{circle.name}</p>

        <div className="mt-5 flex gap-1 p-1 bg-surface-2 rounded-md w-full sm:w-fit overflow-x-auto">
          <TabBtn label="Members" active={tab === "members"} onClick={() => setTab("members")} />
          <TabBtn label="Providers" active={tab === "providers"} onClick={() => setTab("providers")} />
          <TabBtn label="Loved one" active={tab === "loved_one"} onClick={() => setTab("loved_one")} />
          <TabBtn label="Requests" active={tab === "requests"} onClick={() => setTab("requests")} />
        </div>

        <div className="mt-5">
          <Card>
            <CardBody>
              {tab === "members" && <MembersPanel circleId={circle.id} currentUserId={me.user!.id} />}
              {tab === "providers" && <ProvidersPanel circleId={circle.id} />}
              {tab === "loved_one" && <LovedOnePanel circleId={circle.id} />}
              {tab === "requests" && <RequestsPanel circleId={circle.id} isPlatformAdmin={!!me.user?.is_platform_admin} />}
            </CardBody>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-1 sm:flex-none px-4 py-2 rounded text-sm font-medium",
        active ? "bg-surface text-text shadow-sm" : "text-text-muted hover:text-text",
      )}
    >
      {label}
    </button>
  );
}
